/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the hook to use randomised patient columns.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to create a context.
 */
import * as React from 'react';

/*
 * Used to type columns
 */
import { Column } from 'material-table';
import { Chip, makeStyles } from '@material-ui/core';

/*
 * Used to format datetimes
 */
import { DateTime } from 'luxon';



/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../api/dtos';

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const useStyles = makeStyles(theme => ({
    chip: {
        backgroundColor: '#987ba7',
        color: theme.palette.common.white,
    },

}));

const useScreenFailuresColumns = (screenFailures: Dtos.ScreenFailure[] | null, institutions: Dtos.IInstitution[] | null, showSite: boolean): Column<Dtos.ScreenFailure>[] => {
    const classes = useStyles();
    const columns = React.useMemo(() => {
        const c: (Column<Dtos.ScreenFailure> | boolean)[] = [
            {
                field: 'studyNumber',
                title: 'Study Number',
                render: rowData => (
                    rowData?.patient?.studyNumber
                ),
                width: 210
            },
            showSite && {
                field: 'institutionCode',
                title: 'Site Code',
                render: rowData => (
                    <Chip
                        label={institutions?.find(i => i.id === rowData?.patient?.institutionId)?.code}
                        className={classes.chip}
                        size="small"
                    />
                ),
                width: 210
            },
            showSite && {
                field: 'institutionId',
                title: 'Institution',
                render: rowData => (
                    institutions?.find(i => i.id === rowData?.patient?.institutionId)?.name
                )
            },
            {
                field: 'screeningDate',
                title: 'Screening Date',
                render: rowData => (
                    rowData?.screeningDate ? DateTime.fromISO(rowData.screeningDate).toFormat('dd/MM/yyyy') : null
                ),
                width: 210
            },
            {
                field: 'discontinuationDate',
                title: 'Discontinuation Date',
                width: 210,
                render: rowData => (
                    rowData?.discontinuationDate ? DateTime.fromISO(rowData.discontinuationDate).toFormat('dd/MM/yyyy') : null
                )
            }
        ];

        return c.filter(column => !!column) as Column<Dtos.ScreenFailure>[];
    }, [showSite, screenFailures, institutions]);
    
    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default useScreenFailuresColumns;